/**
 * PreparationPage Component
 * TASK-395: Preparation Screen — AI Chat + Tabbed Panel
 *
 * Main container for the meeting preparation screen.
 * Split-panel layout: chat (left) + tabbed panel (right).
 * All data fetched by meetingId from API — no location.state.
 *
 * Data loading: unified fetch on mount (meeting + portfolio + brief)
 * before rendering any tabs. Tabs receive data via props.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Loader2,
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  FileText,
  Lightbulb,
  PieChart,
  TrendingUp,
  CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../../../utils/logger';
import { meetingApi, type MeetingDetails } from '../../../services/api/meetingApi';
import {
  getOrCreateBriefExport,
  getBriefExportLatest,
  ExportPendingBriefError,
  getOrCreatePortfolioExport,
  getPortfolioExportLatest,
  MultiplePortfoliosError,
  type ExportResponse,
  type PortfolioExportView,
} from '../../../services/api/exportApi';
import { portfolioApiService, type PortfolioOverview, type Position, type PerformanceMetrics, type RiskMetrics } from '../../../services/api/portfolioApiService';
import { useProposalStore, getProposalBalance, type ComposerTransaction } from '../../../stores/proposalStore';
import type { RiskDelta, VerifiedConstraint } from '../../../components/portfolio/VerifiedConstraints';
import { useMeetingPrepBridge } from '../../../stores/meetingPrepBridge';
import { useDockStore } from '../../../stores/dockStore';
import { getDockChatStore } from '../../../stores/dockChatStores';
import { useAccount } from '../../../contexts/AccountContext';
import { MeetingSideCards } from '../../dock/MeetingSideCards';
import type { PrepChatPageData } from '../../../types/chat.types';
import { useMeetingPrepSearch } from '../../../hooks/data-gathering/useMeetingPrepSearch';
import type { MeetingPrepSource } from '../../../utils/meetingPrep/types';

import BriefTab from './BriefTab';
import MeetingInsightsTab from './InsightsTab';
import PortfolioTab from './PortfolioTab';
import ProposalsTab from './ProposalsTab';
import SessionExpiryBanner from '../SessionExpiryBanner';
import { useFocusStore } from '../../../stores/focusStore';

// ============================================================================
// Constants
// ============================================================================

type TabId = 'brief' | 'insights' | 'portfolio' | 'proposals';

const TABS: { id: TabId; label: string; icon: React.ReactNode }[] = [
  { id: 'brief', label: 'Brief', icon: <FileText className="w-4 h-4" /> },
  { id: 'insights', label: 'Insights', icon: <Lightbulb className="w-4 h-4" /> },
  { id: 'portfolio', label: 'Portfolio', icon: <PieChart className="w-4 h-4" /> },
  { id: 'proposals', label: 'Proposals', icon: <TrendingUp className="w-4 h-4" /> },
];

// ============================================================================
// Helpers
// ============================================================================

/** Loading step indicator for the preparation loading screen */
const LoadingStep: React.FC<{ done: boolean; loading: boolean; label: string }> = ({ done, loading, label }) => (
  <div className="flex items-center gap-2">
    {done ? (
      <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
    ) : loading ? (
      <Loader2 className="w-4 h-4 text-blue-500 animate-spin flex-shrink-0" />
    ) : (
      <div className="w-4 h-4 rounded-full border-2 border-gray-300 flex-shrink-0" />
    )}
    <span className={done ? 'text-gray-700' : loading ? 'text-blue-600 font-medium' : 'text-gray-400'}>
      {label}
    </span>
  </div>
);

// ============================================================================
// Component
// ============================================================================

// TASK-714/715: one-component-two-mounts. The route shell (`/meeting/:id/prepare`,
// deep links) and the dashboard focus popup render the SAME component; the host
// only changes where "back" goes and how invalid meetings are reported.
export interface PreparationPageProps {
  meetingId?: string;
  host?: 'route' | 'focus';
  /** Focus host: the meeting is invalid/unauthorized/deleted — show the neutral state. */
  onInvalid?: () => void;
}

const PreparationPage: React.FC<PreparationPageProps> = ({
  meetingId: meetingIdProp,
  host = 'route',
  onInvalid,
}) => {
  const { id: routeMeetingId } = useParams<{ id: string }>();
  const meetingId = meetingIdProp ?? routeMeetingId;
  const navigate = useNavigate();

  // ---- Data state ----
  const [meeting, setMeeting] = useState<MeetingDetails | null>(null);
  const [briefData, setBriefData] = useState<Record<string, unknown> | null>(null);
  const [portfolioOverview, setPortfolioOverview] = useState<PortfolioOverview | null>(null);
  const [positions, setPositions] = useState<Position[]>([]);
  const [performanceMetrics, setPerformanceMetrics] = useState<PerformanceMetrics | null>(null);
  const [performanceHistory, setPerformanceHistory] = useState<Array<{ date: string; value: number; benchmark?: number }>>([]);
  const [riskMetrics, setRiskMetrics] = useState<RiskMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [portfolioError, setPortfolioError] = useState<string | null>(null);

  // ---- Brief PPTX preview state (TASK-406) ----
  // Owned here (not in BriefTab) so the regenerate keeps running across
  // tab switches, and there's a single source of truth for the debounce.
  const [previewExport, setPreviewExport] = useState<ExportResponse | null>(null);
  const [previewIsRegenerating, setPreviewIsRegenerating] = useState(false);
  const [previewLanguage, setPreviewLanguage] = useState<'en' | 'de' | 'fr'>('en');
  const previewAbortRef = useRef<AbortController | null>(null);
  const previewDebounceRef = useRef<number | null>(null);

  // ---- Portfolio PPTX preview state (TASK-407) ----
  // Parallel to the brief block above. Regenerate is toolbar-triggered only
  // for v1 (chat doesn't yet edit portfolio data); revisit when chat-portfolio
  // editing lands.
  const [portfolioPreviewExport, setPortfolioPreviewExport] = useState<ExportResponse | null>(null);
  const [portfolioPreviewIsRegenerating, setPortfolioPreviewIsRegenerating] = useState(false);
  const [portfolioPreviewLanguage, setPortfolioPreviewLanguage] = useState<'en' | 'de' | 'fr'>('en');
  const [portfolioPreviewView, setPortfolioPreviewView] = useState<PortfolioExportView>('client');
  const portfolioPreviewAbortRef = useRef<AbortController | null>(null);

  // ---- Loading progress (all must complete before screen renders) ----
  const [meetingLoaded, setMeetingLoaded] = useState(false);
  const [portfolioDone, setPortfolioDone] = useState(false);
  const [briefDone, setBriefDone] = useState(false);

  // ---- Insights (powered by useMeetingPrepSearch) ----
  // Only start search when no existing brief was found after the initial data load.
  // This prevents re-running the full gathering pipeline every time the page is opened.
  const [shouldStartSearch, setShouldStartSearch] = useState(false);
  const hasClient = !!meeting?.client_id;
  const prepSearch = useMeetingPrepSearch({
    meetingId: meetingId ?? '',
    clientId: hasClient ? meeting.client_id : '',
    clientName: hasClient ? meeting.client_name : '',
    meetingDate: meeting?.scheduled_at ? new Date(meeting.scheduled_at) : new Date(),
    autoStart: shouldStartSearch && hasClient,
  });

  // ---- Page readiness: all data sources must complete before rendering ----
  // Wait for searches AND AI insight extraction (not just raw search completion).
  // IMPORTANT: Only block on insightsReady when we still need to *generate* a brief
  // (insightsReady is required by the second useEffect that auto-generates briefs).
  // If a brief already exists (briefData !== null), show the page immediately — insights
  // continue loading in the background and are visible in the InsightsTab.
  const insightsReady = !hasClient || ((prepSearch.isComplete || prepSearch.hasError) && !prepSearch.isExtractingInsights);
  const isPageReady = meetingLoaded && portfolioDone && briefDone && (briefData !== null || insightsReady);
  const insightSourcesDone = prepSearch.sources.filter(s => s.status === 'completed' || s.status === 'error').length;
  const insightSourcesTotal = prepSearch.sources.length;

  // ---- Layout ----
  const [activeTab, setActiveTab] = useState<TabId>('brief');

  // ---- Chat store lifecycle ----
  // TASK-400 C6.1: include `meetingId` in deps so the cleanup runs when navigating
  // between meetings. Previously only `resetChat` was in the array, so the chat +
  // proposal stores leaked state from the prior meeting until the component unmounted.
  useEffect(() => () => { useMeetingPrepBridge.getState().reset(); useProposalStore.getState().reset(); }, [meetingId]);

  // ---- Dispatch AI proposal actions from chat to proposal store (TASK-715) ----
  // Chat lives in the dock's meeting-scope store now. The cursor starts at the
  // CURRENT length: a restored (persisted) thread must never replay composer
  // prefills or proposal mutations from a previous session.
  const { account } = useAccount();
  const useMeetingChatStore = useMemo(
    () => getDockChatStore(account?.id ?? 'unknown', { scopeType: 'meeting', entityId: meetingId ?? 'unknown' }),
    [account?.id, meetingId]
  );
  const chatMessages = useMeetingChatStore((s) => s.messages);
  const mountLenRef = useRef<number>(-1);
  if (mountLenRef.current === -1) mountLenRef.current = useMeetingChatStore.getState().messages.length;
  const lastProcessedProposalIdx = useRef<number>(mountLenRef.current);
  useEffect(() => {
    if (chatMessages.length <= lastProcessedProposalIdx.current) return;
    const { updateProposal, removeProposal, setComposerPrefill } = useProposalStore.getState();

    for (let i = lastProcessedProposalIdx.current; i < chatMessages.length; i++) {
      const msg = chatMessages[i];
      const proposalActions = msg.side?.proposalActions;
      if (msg.role !== 'assistant' || !proposalActions?.length) continue;
      for (const action of proposalActions) {
        if (action.action === 'create' && action.data) {
          // TASK-396: no silent write — pre-fill the composer for advisor review, switch to
          // the Proposals tab. The advisor reviews the simulation and Saves (or discards).
          const d = action.data as {
            title?: string;
            rationale?: string;
            transactions?: ComposerTransaction[];
            // TASK-686: the rails the backend actually checked, carried to the composer so the
            // advisor reviews the figures rather than the model's prose.
            verified_constraints?: VerifiedConstraint[];
            risk_deltas?: RiskDelta[];
          };
          setComposerPrefill({
            transactions: d.transactions ?? [],
            title: d.title || 'AI Proposal',
            notes: d.rationale || '',
            verifiedConstraints: d.verified_constraints,
            riskDeltas: d.risk_deltas,
          });
          setActiveTab('proposals');
        } else if (action.action === 'update' && action.proposalId) {
          updateProposal(action.proposalId, action.data as Record<string, unknown>);
        } else if (action.action === 'remove' && action.proposalId) {
          removeProposal(action.proposalId);
        }
      }
    }
    lastProcessedProposalIdx.current = chatMessages.length;
  }, [chatMessages]);

  const setSwitchTab = useMeetingPrepBridge((s) => s.setSwitchTab);
  useEffect(() => {
    setSwitchTab((tab: string) => {
      if (['brief', 'insights', 'portfolio', 'proposals'].includes(tab)) {
        setActiveTab(tab as TabId);
      }
    });
    return () => setSwitchTab(null);
  }, [setSwitchTab]);

  // ---- TASK-715: the dock speaks for this meeting ----
  // Register the live context provider (parity with the deleted PrepChatPanel's
  // buildContext, typed PrepChatPageData so a key rename fails tsc — the backend
  // reader is markov_prompt_builder._build_meeting_context).
  useEffect(() => {
    if (!meetingId || !meeting) return;
    useDockStore.getState().setContextProvider((): {
      current_route: string;
      entity: { type: 'meeting'; id: string };
      page_data: PrepChatPageData & Record<string, unknown>;
    } => {
      const briefSnapshot = useMeetingPrepBridge.getState().getBriefSnapshotLive();
      const { portfolioSnapshot, proposals } = useProposalStore.getState();
      return {
        current_route: `/meeting/${meetingId}/prepare`,
        entity: { type: 'meeting', id: meetingId },
        page_data: {
          meeting_id: meetingId,
          client_id: meeting.client_id,
          client_name: meeting.client_name,
          meeting_type: meeting.meeting_type,
          advisor_id: meeting.advisor_id,
          scheduled_at: meeting.scheduled_at ?? undefined,
          current_brief: briefSnapshot,
          portfolio: portfolioSnapshot
            ? {
                total_value: portfolioSnapshot.totalValue,
                allocation_by_class: portfolioSnapshot.allocationByClass,
                top_positions: portfolioSnapshot.positions.slice(0, 15),
              }
            : undefined,
          existing_proposals: proposals.map((p) => ({
            id: p.id,
            title: p.title,
            category: p.category,
            rationale: p.rationale,
            balance: getProposalBalance(p),
          })),
        },
      };
    });
    return () => useDockStore.getState().setContextProvider(null);
  }, [meetingId, meeting]);

  // ============================================================================
  // Unified Data Fetching
  // ============================================================================

  useEffect(() => {
    if (!meetingId) return;
    let cancelled = false;

    const loadAllData = async () => {
      setError(null);
      setMeetingLoaded(false);
      setPortfolioDone(false);
      setBriefDone(false);

      // Step 1: Meeting details (required — clientId needed for everything else)
      let details: MeetingDetails;
      try {
        details = await meetingApi.getMeetingDetails(meetingId);
        if (cancelled) return;
        if (!details || !details.session_id) {
          setError('Meeting data is empty or invalid.');
          return;
        }
        setMeeting(details);
        setMeetingLoaded(true);
        // → triggers useMeetingPrepSearch auto-start (insights load in parallel)
      } catch (err) {
        logger.error('[PreparationPage] Failed to load meeting:', err);
        setError('Meeting not found or failed to load.');
        return;
      }

      // Step 2: Portfolio + Brief in parallel (non-fatal individually)
      const [portfolioResult, briefResult] = await Promise.allSettled([
        portfolioApiService.getClientDashboardData(details.client_id),
        meetingApi.getMeetingBrief(meetingId),
      ]);

      if (cancelled) return;

      // Handle portfolio — keep local refs for brief context (state hasn't committed yet)
      let localOverview: PortfolioOverview | null = null;
      let localPositions: Position[] = [];
      let localRisk: RiskMetrics | null = null;
      let localPerformance: PerformanceMetrics | null = null;

      if (portfolioResult.status === 'fulfilled' && portfolioResult.value) {
        const pData = portfolioResult.value;
        localOverview = pData.overview || null;
        localPositions = pData.positions || [];
        localRisk = pData.risk || null;
        localPerformance = pData.performance || null;
        setPortfolioOverview(localOverview);
        setPositions(localPositions);
        setPerformanceMetrics(localPerformance);
        setPerformanceHistory(pData.performanceHistory || []);
        setRiskMetrics(localRisk);
        if (pData.overview) {
          useProposalStore.getState().setPortfolioSnapshot({
            clientId: details.client_id,
            totalValue: pData.overview.total_value,
            positions: (pData.positions || []).map((p: Position) => ({
              isin: p.isin,
              name: p.security_name,
              value: p.market_value,
              weight: pData.overview!.total_value > 0 ? (p.market_value / pData.overview!.total_value) * 100 : 0,
              assetClass: p.asset_class || '',
            })),
            allocationByClass: (pData.overview.allocation_by_asset_class as Record<string, number>) || {},
          });
        }
      } else {
        const reason = portfolioResult.status === 'rejected' ? portfolioResult.reason?.message || 'Unknown error' : 'No data returned';
        logger.warn('[PreparationPage] Portfolio data unavailable:', reason);
        setPortfolioError('Failed to load portfolio data');
      }
      setPortfolioDone(true);

      if (cancelled) return;

      // Handle brief — if one already exists, use it and skip search entirely.
      // Otherwise, trigger search so insights are ready for first-draft generation.
      if (briefResult.status === 'fulfilled' && briefResult.value && !briefResult.value.error) {
        setBriefData(briefResult.value);
        setBriefDone(true);
        // Brief exists — no gathering needed; page is ready immediately
      } else {
        // No brief yet — start search so the auto-generation effect has insights
        setShouldStartSearch(true);
      }
      // Page readiness computed from meetingLoaded + portfolioDone + briefDone + searchesDone
    };

    loadAllData();
    return () => { cancelled = true; };
  }, [meetingId]);

  // TASK-400 C6.2: first-draft brief generation, deferred until insights are ready.
  // The previous implementation ran inside `loadAllData` before insights finished
  // loading, so the auto-generated brief never contained them and users saw a
  // stripped-down draft that required a manual regenerate.
  useEffect(() => {
    if (!meetingId || !meeting || briefDone || briefLoading) return;
    if (!insightsReady) return; // wait for prepSearch to finish (or error out)
    let cancelled = false;

    (async () => {
      setBriefLoading(true);
      try {
        await meetingApi.generateBrief(meetingId, {
          portfolio: portfolioOverview ? {
            overview: portfolioOverview,
            positions: positions.slice(0, 10),
            riskMetrics,
            performanceMetrics,
          } : undefined,
          insights: prepSearch.insights.length > 0
            ? prepSearch.insights.map(i => ({ title: i.title, description: i.description, category: i.category }))
            : undefined,
          clientProfile: {
            client_name: meeting.client_name,
            client_id: meeting.client_id,
            meeting_type: meeting.meeting_type,
          },
        });
        if (cancelled) return;
        const generatedBrief = await meetingApi.getMeetingBrief(meetingId);
        if (!cancelled && generatedBrief && !generatedBrief.error) {
          setBriefData(generatedBrief);
        }
      } catch (briefErr) {
        if (!cancelled) {
          logger.warn('[PreparationPage] Auto brief generation failed:', briefErr);
          toast.error('Brief generation failed — you can retry from the Brief tab');
        }
      } finally {
        if (!cancelled) {
          setBriefLoading(false);
          setBriefDone(true);
        }
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insightsReady, meetingId, meeting, briefDone]);

  // ---- Brief refresh (full AI regeneration — "Regenerate" button) ----
  // ---- PPTX preview regenerate (TASK-406) ----
  // Declared above handleSectionSaved so the debounce trigger is in scope.
  // TASK-407: helper used by both brief + portfolio regenerate flows. When
  // the route returns 202 (status="generating") because an LLM call is
  // already in-flight for the same scope, poll the /latest endpoint until a
  // ready row appears. The in-flight call is producing it; we just don't own
  // that XHR anymore.
  const pollLatest = useCallback(
    async (
      fetcher: () => Promise<ExportResponse | null>,
      signal: AbortSignal,
      currentVersion: number | undefined,
    ): Promise<ExportResponse | null> => {
      const POLL_INTERVAL_MS = 2000;
      const MAX_ATTEMPTS = 90; // ~3 minutes
      for (let i = 0; i < MAX_ATTEMPTS; i++) {
        if (signal.aborted) return null;
        try {
          const latest = await fetcher();
          if (latest && latest.status === 'ready' && (!currentVersion || latest.version > currentVersion)) {
            return latest;
          }
        } catch (err) {
          logger.warn('[PreparationPage] poll /latest failed:', err);
        }
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      }
      return null;
    },
    [],
  );

  const runPreviewRegenerate = useCallback(
    async (opts: { regenerate: boolean }): Promise<void> => {
      if (!meetingId) return;
      previewAbortRef.current?.abort();
      const controller = new AbortController();
      previewAbortRef.current = controller;
      const priorVersion = previewExport?.version;
      setPreviewIsRegenerating(true);
      try {
        const info = await getOrCreateBriefExport(meetingId, {
          language: previewLanguage,
          regenerate: opts.regenerate,
          preview: true,
          signal: controller.signal,
        });
        if (info.status === 'ready') {
          if (!controller.signal.aborted) setPreviewExport(info);
        } else if (!controller.signal.aborted) {
          const ready = await pollLatest(
            () => getBriefExportLatest(meetingId, previewLanguage),
            controller.signal,
            priorVersion,
          );
          if (ready && !controller.signal.aborted) setPreviewExport(ready);
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        if (err instanceof ExportPendingBriefError) {
          // No brief content yet — silently leave preview empty.
          setPreviewExport(null);
        } else {
          logger.warn('[PreparationPage] Preview regenerate failed:', err);
          toast.error('Preview regenerate failed');
        }
      } finally {
        if (previewAbortRef.current === controller) {
          previewAbortRef.current = null;
          setPreviewIsRegenerating(false);
        }
      }
    },
    [meetingId, previewLanguage],
  );

  // 3s debounce trigger — invoked from handleSectionSaved (chat-Apply + manual save).
  const schedulePreviewRegenerate = useCallback(() => {
    if (previewDebounceRef.current) window.clearTimeout(previewDebounceRef.current);
    previewDebounceRef.current = window.setTimeout(() => {
      previewDebounceRef.current = null;
      void runPreviewRegenerate({ regenerate: true });
    }, 3000);
  }, [runPreviewRegenerate]);

  const handlePreviewLanguageChange = useCallback((lang: 'en' | 'de' | 'fr') => {
    setPreviewLanguage(lang);
    setPreviewExport(null); // clear preview while we fetch the new-language export
  }, []);

  const handlePreviewRegenerateNow = useCallback(() => {
    if (previewDebounceRef.current) {
      window.clearTimeout(previewDebounceRef.current);
      previewDebounceRef.current = null;
    }
    void runPreviewRegenerate({ regenerate: true });
  }, [runPreviewRegenerate]);

  // Mount: see if a ready export already exists; if so, render it immediately.
  // Don't regenerate on mount — first user action (or chat Apply) drives that.
  useEffect(() => {
    if (!meetingId) return;
    let cancelled = false;
    (async () => {
      try {
        const existing = await getBriefExportLatest(meetingId, previewLanguage);
        if (!cancelled && existing) setPreviewExport(existing);
      } catch (err) {
        logger.warn('[PreparationPage] getBriefExportLatest failed:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [meetingId, previewLanguage]);

  // Cleanup on meeting change / unmount.
  useEffect(
    () => () => {
      if (previewDebounceRef.current) window.clearTimeout(previewDebounceRef.current);
      previewAbortRef.current?.abort();
      portfolioPreviewAbortRef.current?.abort();
    },
    [meetingId],
  );

  // ---- Portfolio PPTX preview (TASK-407, parallel to brief block) ----
  const clientIdForPortfolio = meeting?.client_id;

  const runPortfolioPreviewRegenerate = useCallback(
    async (opts: { regenerate: boolean }): Promise<void> => {
      if (!clientIdForPortfolio) return;
      portfolioPreviewAbortRef.current?.abort();
      const controller = new AbortController();
      portfolioPreviewAbortRef.current = controller;
      setPortfolioPreviewIsRegenerating(true);
      try {
        const info = await getOrCreatePortfolioExport(clientIdForPortfolio, {
          language: portfolioPreviewLanguage,
          view: portfolioPreviewView,
          regenerate: opts.regenerate,
          preview: true,
          session_id: meetingId,
          client_name: meeting?.client_name,
          signal: controller.signal,
        });
        // TASK-407: when an LLM call is already running for the same scope,
        // the backend returns 202 with status="generating" (no pdf_url yet).
        // Poll /latest until a ready row materialises — the in-flight call
        // is producing it; we just don't own that promise anymore.
        if (info.status === 'ready') {
          if (!controller.signal.aborted) setPortfolioPreviewExport(info);
        } else if (!controller.signal.aborted) {
          const priorVersion = portfolioPreviewExport?.version;
          const ready = await pollLatest(
            () => getPortfolioExportLatest(clientIdForPortfolio, portfolioPreviewLanguage),
            controller.signal,
            priorVersion,
          );
          if (ready && !controller.signal.aborted) setPortfolioPreviewExport(ready);
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        if (err instanceof MultiplePortfoliosError) {
          const names = err.response.portfolios
            .map((p) => (p as { name?: string }).name || p.id)
            .filter(Boolean)
            .join(', ');
          toast.error(`Multiple portfolios for this client. Pick one: ${names}`);
        } else {
          logger.warn('[PreparationPage] Portfolio preview regenerate failed:', err);
          toast.error('Portfolio deck regenerate failed');
        }
      } finally {
        if (portfolioPreviewAbortRef.current === controller) {
          portfolioPreviewAbortRef.current = null;
          setPortfolioPreviewIsRegenerating(false);
        }
      }
    },
    [clientIdForPortfolio, portfolioPreviewLanguage, portfolioPreviewView, meetingId, meeting?.client_name, portfolioPreviewExport?.version, pollLatest],
  );

  const handlePortfolioPreviewLanguageChange = useCallback((lang: 'en' | 'de' | 'fr') => {
    setPortfolioPreviewLanguage(lang);
    setPortfolioPreviewExport(null);
  }, []);

  const handlePortfolioPreviewViewChange = useCallback((view: PortfolioExportView) => {
    setPortfolioPreviewView(view);
    setPortfolioPreviewExport(null);
  }, []);

  const handlePortfolioPreviewRegenerateNow = useCallback(() => {
    void runPortfolioPreviewRegenerate({ regenerate: true });
  }, [runPortfolioPreviewRegenerate]);

  // Mount: hydrate from existing ready row, don't regenerate automatically.
  useEffect(() => {
    if (!clientIdForPortfolio) return;
    let cancelled = false;
    (async () => {
      try {
        const existing = await getPortfolioExportLatest(clientIdForPortfolio, portfolioPreviewLanguage);
        if (!cancelled && existing) setPortfolioPreviewExport(existing);
      } catch (err) {
        logger.warn('[PreparationPage] getPortfolioExportLatest failed:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientIdForPortfolio, portfolioPreviewLanguage]);

  const handleRefreshBrief = useCallback(async () => {
    if (!meetingId) return;
    setBriefLoading(true);
    try {
      await meetingApi.generateBrief(meetingId, {
        portfolio: portfolioOverview ? {
          overview: portfolioOverview,
          positions: positions.slice(0, 10),
          riskMetrics,
          performanceMetrics,
        } : undefined,
        insights: prepSearch.insights.length > 0
          ? prepSearch.insights.map(i => ({ title: i.title, description: i.description, category: i.category }))
          : undefined,
        clientProfile: meeting ? {
          client_name: meeting.client_name,
          client_id: meeting.client_id,
          meeting_type: meeting.meeting_type,
        } : undefined,
      });
      const brief = await meetingApi.getMeetingBrief(meetingId);
      if (brief) setBriefData(brief);
      toast.success('Brief regenerated');
    } catch (err) {
      logger.error('[PreparationPage] Brief generation failed:', err);
      toast.error('Failed to generate brief');
    } finally {
      setBriefLoading(false);
    }
  }, [meetingId, portfolioOverview, positions, riskMetrics, performanceMetrics, meeting, prepSearch.insights]);

  // ---- Brief re-fetch (read-only — after section save or AI update) ----
  // Also triggers a debounced PPTX preview regenerate (TASK-406): 3s after
  // the latest `onSectionSaved`, regenerate the deck. Newer saves reset the
  // timer; in-flight regenerates are aborted via AbortController.
  const handleSectionSaved = useCallback(async () => {
    if (!meetingId) return;
    try {
      const brief = await meetingApi.getMeetingBrief(meetingId);
      if (brief && !brief.error) setBriefData(brief);
    } catch (err) {
      logger.warn('[PreparationPage] Brief re-fetch failed:', err);
    }
    schedulePreviewRegenerate();
  }, [meetingId, schedulePreviewRegenerate]);

  // ---- Brief snapshot for chat context ----
  const setGetBriefSnapshot = useMeetingPrepBridge((s) => s.setGetBriefSnapshot);
  useEffect(() => {
    setGetBriefSnapshot(() => briefData);
    return () => setGetBriefSnapshot(null);
  }, [briefData, setGetBriefSnapshot]);

  // ============================================================================
  // Navigation
  // ============================================================================

  const handleStartMeeting = useCallback(() => {
    if (!meetingId) return;
    navigate(`/meeting/${meetingId}/live`);
  }, [meetingId, navigate]);

  const handleBack = useCallback(() => {
    if (host === 'focus') {
      // In the popup, "back" is a close REQUEST (TASK-714) — never a route change.
      void useFocusStore.getState().requestClose();
      return;
    }
    navigate('/dashboard');
  }, [navigate, host]);

  // Focus host: a meeting that fails to load reports invalid instead of showing a
  // dead-end error screen inside the popup (neutral state, no permission oracle).
  useEffect(() => {
    if (host === 'focus' && error) onInvalid?.();
  }, [host, error, onInvalid]);

  // ============================================================================
  // TASK-715: proposal-card feed — cards from THIS session's dock turns
  // (task/action proposals + blocked portfolio proposals). Brief updates render
  // inline in BriefTab; create/update/remove actions are consumed by the bridge.
  // ============================================================================

  const sessionSideCards = useMemo(() => {
    const recent = chatMessages.slice(mountLenRef.current === -1 ? 0 : mountLenRef.current);
    const cards = recent.filter(
      (m) =>
        m.role === 'assistant' &&
        ((m.side?.actionProposals?.length ?? 0) > 0 ||
          (m.side?.proposalActions ?? []).some((p) => p.action === 'blocked'))
    );
    if (cards.length === 0) return null;
    return (
      <div className="max-h-56 shrink-0 space-y-2 overflow-y-auto border-b border-gray-200 bg-gray-50 px-4 py-2">
        {cards.map((m) => (
          <MeetingSideCards
            key={m.id}
            actionProposals={m.side?.actionProposals}
            proposalActions={m.side?.proposalActions}
            onRevise={(prompt) => useDockStore.getState().requestPrompt(prompt)}
          />
        ))}
      </div>
    );
  }, [chatMessages]);

  // ============================================================================
  // Render: Loading / Error States
  // ============================================================================

  if (!meetingId) {
    return (
      <div className={`flex items-center justify-center ${host === "focus" ? "h-full" : "h-screen"} bg-gray-50`}>
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Missing Meeting ID</h2>
          <button onClick={handleBack} className="flex items-center gap-2 px-4 py-2 mx-auto text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center ${host === "focus" ? "h-full" : "h-screen"} bg-gray-50`}>
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Failed to Load</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button onClick={handleBack} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!isPageReady) {
    return (
      <div className={`flex items-center justify-center ${host === "focus" ? "h-full" : "h-screen"} bg-gray-50`}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Preparing Meeting</h2>
          <p className="text-sm text-gray-500 mb-4">Loading all data sources...</p>
          <div className="space-y-2 text-xs text-left max-w-xs mx-auto">
            <LoadingStep done={meetingLoaded} loading={!meetingLoaded} label="Meeting details" />
            <LoadingStep done={portfolioDone} loading={meetingLoaded && !portfolioDone} label="Client portfolio" />
            <LoadingStep done={briefDone} loading={meetingLoaded && !briefDone} label={briefLoading ? 'Generating brief...' : 'Meeting brief'} />
            {hasClient && (
              <LoadingStep
                done={insightsReady}
                loading={!insightsReady}
                label={
                  prepSearch.isExtractingInsights
                    ? 'Analyzing findings...'
                    : `Gathering intelligence${insightSourcesTotal > 0 ? ` (${insightSourcesDone}/${insightSourcesTotal})` : ''}`
                }
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!meeting) {
    return null;
  }

  // ============================================================================
  // Main Layout
  // ============================================================================

  return (
    <div className={`flex flex-col ${host === "focus" ? "h-full" : "h-screen"} bg-gray-100`}>
      <SessionExpiryBanner />
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={handleBack} className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-sm font-semibold text-gray-900">Meeting Preparation</h1>
            <p className="text-xs text-gray-500">{meeting.client_name} — {meeting.meeting_type.replace(/_/g, ' ')}</p>
          </div>
        </div>
        <button onClick={handleStartMeeting} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors">
          Start Meeting <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* TASK-715: the left chat panel is gone — the dock (bottom bar on the route
          shell, dashboard InputDock in the focus popup) carries the conversation.
          Tabs go full-width. New proposal cards from this session's turns render
          in a feed above the tabs (the dock sheet shows them too on the route). */}
      {sessionSideCards}
      <div className={`flex flex-1 flex-col overflow-hidden bg-white ${host === 'route' ? 'pb-14' : ''}`}>
        <div className="flex-1 flex flex-col overflow-hidden bg-white relative">
          {/* Tab bar */}
          <div className="flex border-b border-gray-200 flex-shrink-0 bg-white px-2">
            {TABS.map((tab) => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id ? 'text-blue-600 border-blue-600' : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'}`}>
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="flex-1 overflow-hidden">
            {activeTab === 'brief' && (
              <BriefTab
                meetingId={meetingId!}
                clientName={meeting.client_name}
                meetingType={meeting.meeting_type}
                briefData={briefData}
                isLoading={briefLoading}
                onRefresh={handleRefreshBrief}
                onSectionSaved={handleSectionSaved}
                previewExport={previewExport}
                previewIsRegenerating={previewIsRegenerating}
                previewLanguage={previewLanguage}
                onPreviewLanguageChange={handlePreviewLanguageChange}
                onPreviewRegenerate={handlePreviewRegenerateNow}
              />
            )}
            {activeTab === 'insights' && (
              <MeetingInsightsTab
                isLoading={prepSearch.isLoading}
                isComplete={prepSearch.isComplete}
                hasError={prepSearch.hasError}
                isExtractingInsights={prepSearch.isExtractingInsights}
                insights={prepSearch.insights}
                summary={prepSearch.summary}
                sourceSummary={prepSearch.sources.map((s: MeetingPrepSource) => ({
                  type: s.type,
                  count: s.itemCount,
                  status: s.status === 'completed' ? 'completed' as const : s.status === 'error' ? 'error' as const : 'loading' as const,
                }))}
                onRetry={prepSearch.refreshAll}
              />
            )}
            {activeTab === 'portfolio' && (
              <PortfolioTab
                overview={portfolioOverview}
                positions={positions}
                performanceMetrics={performanceMetrics}
                performanceHistory={performanceHistory}
                riskMetrics={riskMetrics}
                isLoading={false}
                error={portfolioError}
                clientId={meeting.client_id}
                previewExport={portfolioPreviewExport}
                previewIsRegenerating={portfolioPreviewIsRegenerating}
                previewLanguage={portfolioPreviewLanguage}
                previewView={portfolioPreviewView}
                onPreviewLanguageChange={handlePortfolioPreviewLanguageChange}
                onPreviewViewChange={handlePortfolioPreviewViewChange}
                onPreviewRegenerate={handlePortfolioPreviewRegenerateNow}
              />
            )}
            {activeTab === 'proposals' && (
              <ProposalsTab
                meetingId={meetingId}
                clientId={meeting.client_id}
              />
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default PreparationPage;
